<template>
  <!-- AI 어시스턴트 플로팅 위젯 — 토스페이먼츠 상담창 패턴, 빌리언대부 톤 (UI Shell) -->
  <div class="cl-ai-root">
    <!-- 챗 패널 -->
    <div v-if="open" class="cl-ai-panel" role="dialog" aria-label="AI 대출상담사 링키">
      <button type="button" class="cl-ai-close" @click="open = false" aria-label="닫기">×</button>

      <div ref="logEl" class="cl-ai-body">
        <div
          v-for="(m, idx) in messages"
          :key="idx"
          class="cl-ai-msg"
          :class="m.role === 'user' ? 'cl-ai-msg-user' : 'cl-ai-msg-bot'"
        >
          <!-- 봇 메시지: 좌측 AI 아바타 + 말풍선 + 빠른답변 버튼 -->
          <template v-if="m.role === 'bot'">
            <div class="cl-ai-avatar">링키</div>
            <div class="cl-ai-msg-col">
              <div class="cl-ai-bubble cl-ai-bubble-bot">{{ m.text }}</div>
              <div v-if="m.buttons && m.buttons.length" class="cl-ai-quick">
                <button
                  v-for="btn in m.buttons"
                  :key="btn"
                  type="button"
                  class="cl-ai-quick-btn"
                  @click="onQuickClick(btn)"
                >
                  {{ btn }}
                </button>
              </div>
              <!-- 상담봇 되묻기 선택지 (카드 제목) -->
              <div v-if="m.cards && m.cards.length" class="cl-ai-quick">
                <button
                  v-for="c in m.cards"
                  :key="c.cardId"
                  type="button"
                  class="cl-ai-quick-btn"
                  @click="onCardClick(c)"
                >
                  {{ c.title }}
                </button>
              </div>
              <button
                v-if="m.showHome"
                type="button"
                class="cl-ai-home-btn"
                @click="resetConversation"
              >
                처음으로
              </button>
              <div v-if="m.time" class="cl-ai-time cl-ai-time-bot">{{ m.time }}</div>
            </div>
          </template>

          <!-- 사용자 메시지: 우측 말풍선 -->
          <template v-else>
            <div class="cl-ai-msg-col cl-ai-msg-col-user">
              <div class="cl-ai-bubble cl-ai-bubble-user">{{ m.text }}</div>
              <div v-if="m.time" class="cl-ai-time cl-ai-time-user">{{ m.time }}</div>
            </div>
          </template>
        </div>
      </div>

      <form class="cl-ai-input-row" @submit.prevent="onSend">
        <input
          v-model="input"
          type="text"
          class="cl-ai-input"
          placeholder="로그인후 정확한 상담이 가능합니다"
          maxlength="500"
          autocomplete="off"
        />
        <button type="submit" class="cl-ai-send" :disabled="!input.trim() || loading" aria-label="전송">
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path d="M3 11.5L21 3l-8.5 18-2.2-7.3L3 11.5z" fill="currentColor"/>
          </svg>
        </button>
      </form>
    </div>

    <!-- 플로팅 버튼 -->
    <button
      type="button"
      class="cl-ai-fab"
      :aria-expanded="open"
      aria-label="AI 대출상담사 링키 열기"
      @click="toggle"
    >
      AI
    </button>
  </div>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue';

const open = ref(false);
const input = ref('');
const logEl = ref(null);
const loading = ref(false);

// 로그인 여부만 프론트에서 판단(버튼 노출용) — 고객 식별자는 서버가 세션에서 주입한다
const isLoggedIn = computed(() => !!sessionStorage.getItem('custId'));

const QUICK_BUTTONS = ['대출 한도 조회', '대출 신청 문의', '이자 납부 문의'];

const QUICK_REPLIES = {
  '대출 한도 조회':
    '안녕하세요, 빌리언대부입니다. 대출 한도는 보유 종목·계좌 등급·매칭테이블에 따라 산정됩니다. ' +
    '회원가입 후 [내정보 > 대출가능금액 조회] 메뉴에서 즉시 확인하실 수 있어요.',
  '대출 신청 문의':
    '안녕하세요, 빌리언대부입니다. 대출 신청은 [대출신청] 메뉴에서 진행하실 수 있습니다. ' +
    '본인인증·증권사 계좌 연동·약관 동의 단계를 거쳐 신청 완료 시 영업일 기준 당일 실행됩니다.',
  '이자 납부 문의':
    '안녕하세요, 빌리언대부입니다. 이자는 매월 약정일에 등록된 계좌에서 자동 출금됩니다. ' +
    '출금이 어려운 경우 약정일 전일까지 [이자스케줄] 메뉴에서 선납 가능합니다.'
};

const greet = () => ([
  {
    role: 'bot',
    text:
      '안녕하세요, 빌리언대부 AI 대출상담사 링키예요.\n\n' +
      '빌리언대부 고객센터 상담 가능 시간은 평일 08:00 - 19:00 예요.\n' +
      '(점심시간 12:00 - 13:00 미운영 / 주말 및 공휴일 휴무)\n\n' +
      '고객센터 운영시간 종료 시, 진행 중인 채팅 상담이 자동 종료될 수 있는 점 너른 양해 부탁드립니다.\n\n' +
      '링키와 연결되었어요. 궁금하신 내용의 버튼을 선택하거나 질문을 입력해 주세요.',
    buttons: [...QUICK_BUTTONS],
    time: nowLabel()
  }
]);

const messages = ref(greet());

function nowLabel() {
  const d = new Date();
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  const ampm = h < 12 ? '오전' : '오후';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${ampm} ${h12}:${m}`;
}

const toggle = () => {
  open.value = !open.value;
  if (open.value) nextTick(scrollToBottom);
};

const onQuickClick = (btn) => {
  messages.value.push({ role: 'user', text: btn, time: nowLabel() });
  nextTick(scrollToBottom);
  setTimeout(() => {
    messages.value.push({
      role: 'bot',
      text: QUICK_REPLIES[btn] || '준비 중입니다.',
      showHome: true,
      time: nowLabel()
    });
    nextTick(scrollToBottom);
  }, 300);
};

const onSend = async () => {
  const text = input.value.trim();
  if (!text || loading.value) return;
  messages.value.push({ role: 'user', text, time: nowLabel() });
  input.value = '';
  nextTick(scrollToBottom);

  // 본인 계좌 기준 상담이므로 로그인 없이는 백엔드 호출하지 않는다
  if (!isLoggedIn.value) {
    setTimeout(() => {
      messages.value.push({
        role: 'bot',
        text:
          '정확한 상담을 위해 로그인이 필요합니다.\n' +
          '로그인 후 계좌등급·대출가능금액·종목 담보 적격성 등을 바로 확인하실 수 있어요.',
        showHome: true,
        time: nowLabel()
      });
      nextTick(scrollToBottom);
    }, 300);
    return;
  }

  await askBot({ message: text });
};

// 되묻기 선택지(카드 제목) 클릭 — 사용자 발화로 표시 후 해당 카드 답변 요청
const onCardClick = async (card) => {
  if (loading.value) return;
  messages.value.push({ role: 'user', text: card.title, time: nowLabel() });
  nextTick(scrollToBottom);
  await askBot({ cardId: card.cardId });
};

// 상담봇 호출 (로직 우선, AI 미호출 — Spring 단독, 대표 확정 2026-09-01)
// 답변은 검수 확정된 안내 카드 원문 그대로. 고객 식별자는 서버가 세션에서 주입한다.
const askBot = async (payload) => {
  loading.value = true;
  try {
    const res = await fetch('/api/loan/bot/ask', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (res.status === 401) {
      messages.value.push({
        role: 'bot',
        text: '세션이 만료되었습니다. 다시 로그인해 주세요.',
        showHome: true,
        time: nowLabel()
      });
      return;
    }
    const data = await res.json();
    if (data.type === 'ANSWER') {
      messages.value.push({ role: 'bot', text: data.answer, showHome: true, time: nowLabel() });
    } else if (data.type === 'CHOICES') {
      messages.value.push({
        role: 'bot',
        text: data.message,
        cards: data.choices || [],
        time: nowLabel()
      });
    } else {
      messages.value.push({
        role: 'bot',
        text: data.message || data.resultMessage || '응답을 받지 못했습니다. 다시 시도해 주세요.',
        showHome: true,
        time: nowLabel()
      });
    }
  } catch (e) {
    messages.value.push({
      role: 'bot',
      text: '상담 서버 연결에 문제가 있습니다. 잠시 후 다시 시도해 주세요.\n고객센터 1833-8996 (평일 09:00~18:00)',
      showHome: true,
      time: nowLabel()
    });
  } finally {
    loading.value = false;
    nextTick(scrollToBottom);
  }
};

const resetConversation = () => {
  messages.value = greet();
  nextTick(scrollToBottom);
};

const scrollToBottom = () => {
  const el = logEl.value;
  if (el) el.scrollTop = el.scrollHeight;
};
</script>

<style scoped>
/* 빌리언대부 AI 상담 위젯 — 단일 hue(블루) 톤 변주, 둥근 모서리 (전무님 요청에 따라 프로젝트 기본 샤프 코너 규칙을 본 위젯에 한해 예외 적용) */
.cl-ai-root {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 1080;
}

/* 플로팅 버튼 — 원형, 단색 블루, "AI" 볼드체 */
.cl-ai-fab {
  width: 56px;
  height: 56px;
  background: #3182F6;
  color: #fff;
  border: none;
  border-radius: 50%;
  font-weight: 800;
  font-size: 16px;
  letter-spacing: 0.04em;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(49, 130, 246, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
}

.cl-ai-fab:hover {
  background: #1B64DA;
}

.cl-ai-fab:focus-visible {
  outline: 3px solid #A7C7FB;
  outline-offset: 2px;
}

/* 패널 */
.cl-ai-panel {
  position: absolute;
  right: 0;
  bottom: 72px;
  width: 360px;
  height: 520px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.cl-ai-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  color: #3182F6;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  z-index: 1;
}

.cl-ai-close:hover {
  color: #1B64DA;
}

/* 채팅 영역 */
.cl-ai-body {
  flex: 1;
  overflow-y: auto;
  padding: 44px 14px 12px;
  background: #fff;
}

.cl-ai-msg {
  display: flex;
  margin-bottom: 14px;
}

.cl-ai-msg-bot {
  justify-content: flex-start;
  gap: 8px;
}

.cl-ai-msg-user {
  justify-content: flex-end;
}

.cl-ai-msg-col {
  display: flex;
  flex-direction: column;
  max-width: 78%;
  gap: 6px;
}

.cl-ai-msg-col-user {
  align-items: flex-end;
}

/* AI 아바타 (봇 발화 측) */
.cl-ai-avatar {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #E8F2FF;
  color: #3182F6;
  font-weight: 800;
  font-size: 11px;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 말풍선 공통 */
.cl-ai-bubble {
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}

.cl-ai-bubble-bot {
  background: #F2F4F7;
  color: #191F28;
  border-radius: 4px 14px 14px 14px;
}

.cl-ai-bubble-user {
  background: #3182F6;
  color: #fff;
  border-radius: 14px 4px 14px 14px;
}

/* 빠른 답변 버튼 */
.cl-ai-quick {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.cl-ai-quick-btn {
  text-align: center;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #C9D6E8;
  color: #3182F6;
  font-size: 13px;
  font-weight: 600;
  border-radius: 999px;
  cursor: pointer;
}

.cl-ai-quick-btn:hover {
  background: #F0F6FF;
  border-color: #3182F6;
}

/* "처음으로" 버튼 */
.cl-ai-home-btn {
  align-self: flex-start;
  padding: 6px 12px;
  background: #fff;
  border: 1px solid #DCE3EE;
  color: #4A5568;
  font-size: 12px;
  border-radius: 999px;
  cursor: pointer;
}

.cl-ai-home-btn:hover {
  background: #F2F4F7;
}

/* 타임스탬프 */
.cl-ai-time {
  font-size: 11px;
  color: #8B95A1;
}

.cl-ai-time-user {
  margin-right: 2px;
}

.cl-ai-time-bot {
  margin-left: 2px;
}

/* 입력 영역 */
.cl-ai-input-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px 12px;
  background: #fff;
  border-top: 1px solid #EEF2F7;
}

.cl-ai-input {
  flex: 1;
  height: 38px;
  padding: 0 14px;
  border: 1px solid #DCE3EE;
  border-radius: 999px;
  font-size: 13px;
  color: #191F28;
  background: #F8FAFC;
}

.cl-ai-input::placeholder {
  color: #B0B8C1;
}

.cl-ai-input:focus {
  outline: none;
  border-color: #3182F6;
  background: #fff;
}

.cl-ai-send {
  width: 38px;
  height: 38px;
  background: #3182F6;
  color: #fff;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cl-ai-send:hover {
  background: #1B64DA;
}

.cl-ai-send:disabled {
  background: #C9D6E8;
  cursor: not-allowed;
}
</style>
